import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const envelopeSchema = z.object({
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  signature: z.string().regex(/^0x[a-fA-F0-9]+$/),
  message: z.string().min(1).max(4000),
});

const hexTx = z.string().regex(/^0x[a-fA-F0-9]{64}$/).optional().nullable();
const weiStr = z.string().regex(/^\d{1,80}$/);
const uuid = z.string().uuid();

// ----- create_drop -----
const createDropInput = z.object({
  env: envelopeSchema,
  drop: z.object({
    name: z.string().trim().min(2).max(80),
    symbol: z.string().trim().min(2).max(10).regex(/^[A-Za-z0-9]+$/),
    description: z.string().trim().max(1000).nullable().optional(),
    logo_url: z.string().trim().url().max(2000).nullable().optional(),
    chain_id: z.number().int().positive(),
    mint_price_wei: weiStr,
    max_supply: z.number().int().min(1).max(10_000_000).nullable().optional(),
    start_at: z.string().datetime().nullable().optional(),
    end_at: z.string().datetime().nullable().optional(),
  }),
});

export const createDrop = createServerFn({ method: "POST" })
  .inputValidator((v) => createDropInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "create_drop");
    // Bind payload fields to the signed message to prevent parameter swap
    const p = v.payload as Record<string, unknown>;
    if (p.name !== data.drop.name || p.symbol !== data.drop.symbol || p.chain_id !== data.drop.chain_id || p.mint_price_wei !== data.drop.mint_price_wei) {
      throw new Error("Signed payload does not match request");
    }
    const { data: row, error } = await supabaseAdmin.from("nft_drops").insert({
      creator_wallet: v.wallet,
      name: data.drop.name,
      symbol: data.drop.symbol.toUpperCase(),
      description: data.drop.description ?? null,
      logo_url: data.drop.logo_url ?? null,
      chain_id: data.drop.chain_id,
      mint_price_wei: data.drop.mint_price_wei,
      max_supply: data.drop.max_supply ?? null,
      start_at: data.drop.start_at ?? null,
      end_at: data.drop.end_at ?? null,
      status: "live",
    }).select().single();
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("activities").insert({
      kind: "drop_created",
      actor_wallet: v.wallet,
      drop_id: row.id,
      chain_id: data.drop.chain_id,
    });
    return { id: row.id };
  });

// ----- record_mint -----
const mintInput = z.object({
  env: envelopeSchema,
  drop_id: uuid,
  qty: z.number().int().min(1).max(1000),
  price_wei: weiStr,
  tx_hash: hexTx,
  kind: z.enum(["mint", "mint_request"]),
});

export const recordMint = createServerFn({ method: "POST" })
  .inputValidator((v) => mintInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "mint");
    const p = v.payload as Record<string, unknown>;
    if (p.drop_id !== data.drop_id || p.qty !== data.qty || p.price_wei !== data.price_wei) {
      throw new Error("Signed payload does not match request");
    }
    const { data: drop, error: derr } = await supabaseAdmin.from("nft_drops").select("id, minted_count, max_supply, chain_id, start_at, end_at, mint_price_wei, contract_address").eq("id", data.drop_id).maybeSingle();
    if (derr) throw new Error(derr.message);
    if (!drop) throw new Error("Drop not found");
    const { assertDropMetadataOk } = await import("./metadata-validate.server");
    await assertDropMetadataOk(supabaseAdmin, drop.id);
    const totalPriceWei = BigInt(drop.mint_price_wei) * BigInt(data.qty);
    if (drop.mint_price_wei !== data.price_wei && totalPriceWei !== BigInt(data.price_wei)) {
      // Accept either unit price or total price binding
      throw new Error("Price mismatch");
    }
    const now = Date.now();
    if (drop.start_at && new Date(drop.start_at).getTime() > now) throw new Error("Not started");
    if (drop.end_at && new Date(drop.end_at).getTime() < now) throw new Error("Ended");
    const nextCount = (drop.minted_count ?? 0) + data.qty;
    if (drop.max_supply && nextCount > drop.max_supply) throw new Error("Would exceed max supply");

    // Paid mints (kind='mint') on a contract-backed drop MUST be backed by a
    // verified on-chain payment. mint_request (no contract yet) is unpaid demo flow.
    if (data.kind === "mint") {
      if (!drop.contract_address) throw new Error("Drop has no contract; use mint_request");
      if (!data.tx_hash) throw new Error("tx_hash required for paid mint");
      if (totalPriceWei > 0n) {
        const { verifyPayment } = await import("./onchain-verify.server");
        await verifyPayment({
          chainId: drop.chain_id,
          txHash: data.tx_hash,
          from: v.wallet,
          to: drop.contract_address,
          minValueWei: totalPriceWei,
        });
      }
    }

    // Randomly assign unassigned metadata items to this minter (best-effort; ignored if drop has no items).
    const { data: pool } = await supabaseAdmin
      .from("nft_drop_items")
      .select("id, item_index")
      .eq("drop_id", drop.id)
      .is("assigned_wallet", null)
      .limit(500);
    const assignedItems: { id: string; item_index: number }[] = [];
    if (pool && pool.length > 0) {
      const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, data.qty);
      const nowIso = new Date().toISOString();
      for (const it of shuffled) {
        const { error: uErr } = await supabaseAdmin
          .from("nft_drop_items")
          .update({ assigned_wallet: v.wallet, assigned_at: nowIso, assigned_token_id: String(it.item_index) })
          .eq("id", it.id)
          .is("assigned_wallet", null);
        if (!uErr) assignedItems.push(it);
      }
    }

    const { error: aerr } = await supabaseAdmin.from("activities").insert({
      kind: data.kind,
      actor_wallet: v.wallet,
      drop_id: drop.id,
      chain_id: drop.chain_id,
      tx_hash: data.tx_hash ?? null,
      price_wei: data.price_wei,
      metadata: { qty: data.qty, assigned_item_ids: assignedItems.map((i) => i.id) },
    });
    if (aerr) throw new Error(aerr.message);
    const { error: uerr } = await supabaseAdmin.from("nft_drops").update({ minted_count: nextCount }).eq("id", drop.id);
    if (uerr) throw new Error(uerr.message);
    return { ok: true, minted_count: nextCount, assigned_item_ids: assignedItems.map((i) => i.id) };
  });

// ----- add_drop_items (signed by creator; up to 100 total per drop) -----
const itemSchema = z.object({
  name: z.string().trim().max(120).nullable().optional(),
  image_url: z.string().trim().url().max(2000),
  attributes: z.array(z.object({
    trait_type: z.string().max(60),
    value: z.union([z.string().max(120), z.number()]),
  })).max(20).nullable().optional(),
});

const addItemsInput = z.object({
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  drop_id: uuid,
  items: z.array(itemSchema).min(1).max(100),
});

export const addDropItems = createServerFn({ method: "POST" })
  .inputValidator((v) => addItemsInput.parse(v))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const wallet = data.address.toLowerCase();
    const { data: drop, error: derr } = await supabaseAdmin
      .from("nft_drops").select("id, creator_wallet").eq("id", data.drop_id).maybeSingle();
    if (derr) throw new Error(derr.message);
    if (!drop) throw new Error("Drop not found");
    if (drop.creator_wallet.toLowerCase() !== wallet) throw new Error("Not the creator");

    const { count: existing } = await supabaseAdmin
      .from("nft_drop_items").select("id", { count: "exact", head: true }).eq("drop_id", data.drop_id);
    const baseIndex = existing ?? 0;
    if (baseIndex + data.items.length > 100) throw new Error("Cannot exceed 100 items per drop");

    const rows = data.items.map((it, i) => ({
      drop_id: data.drop_id,
      item_index: baseIndex + i,
      name: it.name ?? `#${baseIndex + i + 1}`,
      image_url: it.image_url,
      attributes: it.attributes ?? null,
    }));
    const { error } = await supabaseAdmin.from("nft_drop_items").insert(rows);
    if (error) throw new Error(error.message);
    return { added: rows.length, total: baseIndex + rows.length };
  });

// ----- create_listing -----
const listingInput = z.object({
  env: envelopeSchema,
  drop_id: uuid,
  token_id: z.string().max(80).nullable().optional(),
  price_wei: weiStr,
});

export const createListing = createServerFn({ method: "POST" })
  .inputValidator((v) => listingInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "list");
    const p = v.payload as Record<string, unknown>;
    if (p.drop_id !== data.drop_id || p.price_wei !== data.price_wei) throw new Error("Signed payload does not match request");
    const { data: drop } = await supabaseAdmin.from("nft_drops").select("id, chain_id, contract_address").eq("id", data.drop_id).maybeSingle();
    if (!drop) throw new Error("Drop not found");
    const { assertDropMetadataOk, assertItemMetadataOk } = await import("./metadata-validate.server");
    await assertDropMetadataOk(supabaseAdmin, drop.id);
    if (data.token_id) await assertItemMetadataOk(supabaseAdmin, drop.id, data.token_id);
    const { data: row, error } = await supabaseAdmin.from("listings").insert({
      drop_id: drop.id,
      seller_wallet: v.wallet,
      token_id: data.token_id ?? null,
      chain_id: drop.chain_id,
      contract_address: drop.contract_address,
      price_wei: data.price_wei,
      status: "active",
    }).select().single();
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("activities").insert({
      kind: "list",
      actor_wallet: v.wallet,
      drop_id: drop.id,
      chain_id: drop.chain_id,
      price_wei: data.price_wei,
      metadata: { token_id: data.token_id ?? null, listing_id: row.id },
    });
    return { id: row.id };
  });

// ----- cancel_listing -----
const cancelInput = z.object({
  env: envelopeSchema,
  listing_id: uuid,
});

export const cancelListing = createServerFn({ method: "POST" })
  .inputValidator((v) => cancelInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "cancel_listing");
    const p = v.payload as Record<string, unknown>;
    if (p.listing_id !== data.listing_id) throw new Error("Signed payload does not match request");
    const { data: listing } = await supabaseAdmin.from("listings").select("id, seller_wallet, status").eq("id", data.listing_id).maybeSingle();
    if (!listing) throw new Error("Listing not found");
    if (listing.seller_wallet?.toLowerCase() !== v.wallet) throw new Error("Not the seller");
    if (listing.status !== "active") throw new Error("Listing is not active");
    const { error } = await supabaseAdmin.from("listings").update({ status: "cancelled" }).eq("id", listing.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ----- buy_listing -----
const buyInput = z.object({
  env: envelopeSchema,
  listing_id: uuid,
  tx_hash: hexTx,
});

export const buyListing = createServerFn({ method: "POST" })
  .inputValidator((v) => buyInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "buy");
    const p = v.payload as Record<string, unknown>;
    if (p.listing_id !== data.listing_id) throw new Error("Signed payload does not match request");
    const { data: listing } = await supabaseAdmin.from("listings").select("id, drop_id, chain_id, seller_wallet, price_wei, status, contract_address").eq("id", data.listing_id).maybeSingle();
    if (!listing) throw new Error("Listing not found");
    if (listing.status !== "active") throw new Error("Listing is not available");
    if (listing.seller_wallet?.toLowerCase() === v.wallet) throw new Error("Cannot buy your own listing");

    // Require a verified on-chain payment before flipping the listing to sold.
    if (!data.tx_hash) throw new Error("tx_hash required to complete a purchase");
    const priceWei = BigInt(listing.price_wei);
    if (priceWei > 0n) {
      const recipient = listing.contract_address ?? listing.seller_wallet;
      if (!recipient) throw new Error("Listing has no payment recipient configured");
      const { verifyPayment } = await import("./onchain-verify.server");
      await verifyPayment({
        chainId: listing.chain_id,
        txHash: data.tx_hash,
        from: v.wallet,
        to: recipient,
        minValueWei: priceWei,
      });
    }


    const { error } = await supabaseAdmin.from("listings").update({
      status: "sold",
      buyer_wallet: v.wallet,
      tx_hash: data.tx_hash ?? null,
    }).eq("id", listing.id).eq("status", "active");
    if (error) throw new Error(error.message);
    await supabaseAdmin.from("activities").insert({
      kind: "buy",
      actor_wallet: v.wallet,
      drop_id: listing.drop_id,
      chain_id: listing.chain_id,
      tx_hash: data.tx_hash ?? null,
      price_wei: listing.price_wei,
      metadata: { listing_id: listing.id },
    });
    return { ok: true };
  });

// ----- record admin fee config (signed by admin wallet) -----
const feeConfigInput = z.object({
  env: envelopeSchema,
  chain_id: z.number().int().nullable(),
  mint_bps: z.number().int().min(0).max(10000),
  list_bps: z.number().int().min(0).max(10000),
  creator_bps: z.number().int().min(0).max(10000),
  recipient: z.string().max(80).nullable(),
});

export const recordFeeConfig = createServerFn({ method: "POST" })
  .inputValidator((v) => feeConfigInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "fee_config");
    const p = v.payload as Record<string, unknown>;
    if (p.mint_bps !== data.mint_bps || p.list_bps !== data.list_bps || p.creator_bps !== data.creator_bps) {
      throw new Error("Signed payload does not match request");
    }
    await supabaseAdmin.from("activities").insert({
      kind: "fee_config_signed",
      actor_wallet: v.wallet,
      chain_id: data.chain_id,
      metadata: {
        mint_bps: data.mint_bps,
        list_bps: data.list_bps,
        creator_bps: data.creator_bps,
        recipient: data.recipient,
        signature: data.env.signature,
        message: data.env.message,
      },
    });
    return { ok: true };
  });

// ----- admin: save global fee % + fallback recipient -----
const globalFeesInput = z.object({
  env: envelopeSchema,
  mint_bps: z.number().int().min(0).max(10000),
  list_bps: z.number().int().min(0).max(10000),
  creator_bps: z.number().int().min(0).max(10000),
  fee_recipient: z.string().max(80).nullable(),
});

async function assertAdmin(wallet: string) {
  const { ADMIN_ADDRESSES } = await import("@/config/robotdex");
  if (!ADMIN_ADDRESSES.includes(wallet.toLowerCase())) throw new Error("Not authorized");
}

export const saveGlobalFees = createServerFn({ method: "POST" })
  .inputValidator((v) => globalFeesInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "save_global_fees");
    await assertAdmin(v.wallet);
    const p = v.payload as Record<string, unknown>;
    if (p.mint_bps !== data.mint_bps || p.list_bps !== data.list_bps || p.creator_bps !== data.creator_bps) {
      throw new Error("Signed payload does not match request");
    }
    const { error } = await supabaseAdmin.from("platform_settings").update({
      mint_fee_bps: data.mint_bps,
      list_fee_bps: data.list_bps,
      creator_fee_bps: data.creator_bps,
      fee_recipient: data.fee_recipient,
      updated_at: new Date().toISOString(),
    } as never).eq("id", 1);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ----- admin: save per-network settings (wallet + create fee) -----
const networkEntry = z.object({
  chain_id: z.number().int().positive(),
  fee_recipient: z.string().regex(/^0x[a-fA-F0-9]{40}$/).nullable(),
  create_fee_wei: weiStr,
  enabled: z.boolean().optional(),
});

const networkSettingsInput = z.object({
  env: envelopeSchema,
  entries: z.array(networkEntry).min(1).max(50),
});

export const saveNetworkSettings = createServerFn({ method: "POST" })
  .inputValidator((v) => networkSettingsInput.parse(v))
  .handler(async ({ data }) => {
    const { verifySignedEnvelope } = await import("./wallet-verify.server");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const v = await verifySignedEnvelope(data.env, "save_network_settings");
    await assertAdmin(v.wallet);
    const now = new Date().toISOString();
    for (const e of data.entries) {
      const { error } = await supabaseAdmin.from("platform_network_settings").upsert({
        chain_id: e.chain_id,
        fee_recipient: e.fee_recipient,
        create_fee_wei: e.create_fee_wei,
        enabled: e.enabled ?? true,
        updated_at: now,
      } as never, { onConflict: "chain_id" });
      if (error) throw new Error(error.message);
    }
    return { ok: true, count: data.entries.length };
  });

// ----- track wallet connect (no signature required; just address + upsert) -----
const trackInput = z.object({
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
});

export const trackWalletConnect = createServerFn({ method: "POST" })
  .inputValidator((v) => trackInput.parse(v))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const addr = data.address.toLowerCase();
    const { data: existing } = await supabaseAdmin.from("connected_wallets").select("address, connect_count").eq("address", addr).maybeSingle();
    if (existing) {
      await supabaseAdmin.from("connected_wallets").update({
        last_seen: new Date().toISOString(),
        connect_count: (existing.connect_count ?? 1) + 1,
      }).eq("address", addr);
    } else {
      await supabaseAdmin.from("connected_wallets").insert({ address: addr });
    }
    return { ok: true };
  });

// ----- upload drop asset (server-side write to private drop-assets bucket) -----
const uploadInput = z.object({
  address: z.string().regex(/^0x[a-fA-F0-9]{40}$/),
  folder: z.enum(["logos", "gallery"]),
  content_type: z.literal("image/webp"),
  size: z.number().int().min(1).max(500_000),
  data_b64: z.string().min(1).max(800_000),
});

export const uploadDropAsset = createServerFn({ method: "POST" })
  .inputValidator((v) => uploadInput.parse(v))
  .handler(async ({ data }) => {
    const wallet = data.address.toLowerCase();
    const bytes = Uint8Array.from(atob(data.data_b64), (c) => c.charCodeAt(0));
    if (bytes.byteLength !== data.size) throw new Error("Size mismatch");
    if (bytes.byteLength > 500_000) throw new Error("Too large");
    // Sniff WebP magic: "RIFF"...."WEBP"
    if (
      bytes[0] !== 0x52 || bytes[1] !== 0x49 || bytes[2] !== 0x46 || bytes[3] !== 0x46 ||
      bytes[8] !== 0x57 || bytes[9] !== 0x45 || bytes[10] !== 0x42 || bytes[11] !== 0x50
    ) {
      throw new Error("Only WebP images are accepted");
    }
    const jwt = process.env.PINATA_JWT;
    if (!jwt) throw new Error("Pinata is not configured");
    // Sanity-check the JWT looks like a Pinata token (HS256, 3 parts). If it
    // was pasted with quotes, the "Bearer " prefix, or is another service's
    // token (ES256 etc.), Pinata returns 401 INVALID_CREDENTIALS. Fail fast
    // with a clear message so the admin knows to re-paste it.
    const cleanJwt = jwt.trim().replace(/^Bearer\s+/i, "").replace(/^["']|["']$/g, "");
    const parts = cleanJwt.split(".");
    if (parts.length !== 3) {
      throw new Error("Pinata JWT is malformed. Paste the raw JWT from pinata.cloud (no quotes, no 'Bearer ').");
    }
    try {
      const headerJson = JSON.parse(
        atob(parts[0].replace(/-/g, "+").replace(/_/g, "/")),
      ) as { alg?: string };
      if (headerJson.alg && headerJson.alg !== "HS256") {
        throw new Error(
          `Pinata JWT uses ${headerJson.alg} signing but Pinata only accepts HS256. Create a new key at pinata.cloud → API Keys and update PINATA_JWT.`,
        );
      }
    } catch (e) {
      if (e instanceof Error && e.message.startsWith("Pinata JWT")) throw e;
      // header parse issue — let Pinata be the source of truth
    }
    const filename = `${data.folder}-${wallet}-${crypto.randomUUID()}.webp`;
    const fd = new FormData();
    fd.append("file", new Blob([bytes], { type: "image/webp" }), filename);
    fd.append("pinataMetadata", JSON.stringify({ name: filename, keyvalues: { folder: data.folder, wallet } }));
    fd.append("pinataOptions", JSON.stringify({ cidVersion: 1 }));
    const res = await fetch("https://api.pinata.cloud/pinning/pinFileToIPFS", {
      method: "POST",
      headers: { Authorization: `Bearer ${cleanJwt}` },
      body: fd,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      if (res.status === 401) {
        throw new Error(
          "Pinata rejected the API key (401). Generate a new JWT at pinata.cloud → API Keys with pinFileToIPFS permission and update the PINATA_JWT secret.",
        );
      }
      throw new Error(`Pinata upload failed (${res.status}): ${text.slice(0, 200)}`);
    }
    const json = (await res.json()) as { IpfsHash?: string };
    const cid = json.IpfsHash;
    if (!cid) throw new Error("Pinata did not return a CID");
    const ipfsUri = `ipfs://${cid}`;
    const gatewayUrl = `https://gateway.pinata.cloud/ipfs/${cid}`;
    return { url: ipfsUri, gateway_url: gatewayUrl, cid, path: ipfsUri, size: bytes.byteLength };
  });
